/**
 * Sistema de logging condicional para el frontend.
 *
 * Solo loguea en desarrollo. En producción, los logs son silenciados
 * y los errores pueden ser enviados a servicios de monitoreo externos.
 *
 * Uso:
 *   import logger from 'utils/logger';
 *
 *   logger.log('Mensaje de debug');
 *   logger.error('Error crítico', error);
 *   logger.warn('Advertencia');
 *   logger.info('Información');
 *   logger.table(dataArray);
 *
 * Features:
 *   - Solo loguea en modo desarrollo
 *   - Soporte para integración con Sentry/LogRocket en producción
 *   - API idéntica a console para fácil migración
 *   - Timestamps automáticos
 *   - Colores y formato mejorado
 */

class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === "development";
    this.isProduction = process.env.NODE_ENV === "production";

    // Configuración de integración con servicios externos
    this.sentryEnabled = process.env.REACT_APP_SENTRY_ENABLED === "true";
    this.logRocketEnabled = process.env.REACT_APP_LOGROCKET_ENABLED === "true";
  }

  /**
   * Formatear timestamp para logs
   */
  _getTimestamp() {
    return new Date().toISOString().split("T")[1].split(".")[0];
  }

  /**
   * Agregar contexto adicional al log
   */
  _addContext(...args) {
    if (this.isDevelopment) {
      return [`[${this._getTimestamp()}]`, ...args];
    }
    return args;
  }

  /**
   * Logging normal (debug)
   * Solo visible en desarrollo
   */
  log(...args) {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(...this._addContext(...args));
    }
  }

  /**
   * Logging de debug con estilo
   * Solo visible en desarrollo
   */
  debug(...args) {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.debug(...this._addContext("🐛", ...args));
    }
  }

  /**
   * Logging de información
   * Solo visible en desarrollo
   */
  info(...args) {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.info(...this._addContext("ℹ️", ...args));
    }
  }

  /**
   * Logging de advertencias
   * Visible en desarrollo y producción
   */
  warn(...args) {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.warn(...this._addContext("[WARN]", ...args));
    } else if (this.isProduction) {
      // En producción, podrías enviar a servicio de monitoreo
      this._sendToMonitoring("warning", args);
    }
  }

  /**
   * Logging de errores
   * Visible en desarrollo y producción
   * En producción, envía a servicios de monitoreo
   */
  error(...args) {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.error(...this._addContext("[ERROR]", ...args));
    } else if (this.isProduction) {
      // En producción, enviar a Sentry, LogRocket, etc.
      this._sendToMonitoring("error", args);

      // También loguear en consola para debugging en producción si es necesario
      // eslint-disable-next-line no-console
      console.error(...args);
    }
  }

  /**
   * Logging de éxito (custom)
   * Solo visible en desarrollo
   */
  success(...args) {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(...this._addContext("[OK]", ...args));
    }
  }

  /**
   * Logging de tabla
   * Solo visible en desarrollo
   */
  table(data, columns) {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(this._addContext("[TABLE]")[0]);
      // eslint-disable-next-line no-console
      console.table(data, columns);
    }
  }

  /**
   * Logging de grupo
   * Solo visible en desarrollo
   */
  group(label) {
    if (this.isDevelopment) {
      console.group(label);
    }
  }

  groupCollapsed(label) {
    if (this.isDevelopment) {
      console.groupCollapsed(label);
    }
  }

  groupEnd() {
    if (this.isDevelopment) {
      console.groupEnd();
    }
  }

  /**
   * Timing de operaciones
   * Solo visible en desarrollo
   */
  time(label) {
    if (this.isDevelopment) {
      console.time(label);
    }
  }

  timeEnd(label) {
    if (this.isDevelopment) {
      console.timeEnd(label);
    }
  }

  /**
   * Logging de API calls
   * Útil para debugging de requests/responses
   */
  api(method, url, data, response) {
    if (this.isDevelopment) {
      this.group(`🌐 API ${method.toUpperCase()} ${url}`);
      if (data) this.log("Request:", data);
      if (response) this.log("Response:", response);
      this.groupEnd();
    }
  }

  /**
   * Logging de Redux actions (si se usa Redux)
   */
  action(actionType, payload) {
    if (this.isDevelopment) {
      this.log(`🔄 Action: ${actionType}`, payload);
    }
  }

  /**
   * Enviar a servicios de monitoreo externos
   * @private
   */
  _sendToMonitoring(level, args) {
    // Integración con Sentry
    if (this.sentryEnabled && window.Sentry) {
      if (level === "error") {
        window.Sentry.captureException(args[0]);
      } else {
        window.Sentry.captureMessage(args.join(" "), level);
      }
    }

    // Integración con LogRocket
    if (this.logRocketEnabled && window.LogRocket) {
      window.LogRocket.log(level, ...args);
    }

    // Aquí podrías agregar otras integraciones:
    // - DataDog
    // - New Relic
    // - Custom analytics endpoint
  }

  /**
   * Método para capturar excepciones no manejadas
   */
  captureException(error, context = {}) {
    this.error("Unhandled Exception:", error, context);

    if (this.isProduction && window.Sentry) {
      window.Sentry.captureException(error, { extra: context });
    }
  }

  /**
   * Logging de performance
   */
  performance(label, duration) {
    if (this.isDevelopment) {
      this.log(`⚡ Performance - ${label}: ${duration}ms`);
    }
  }

  /**
   * Deshabilitar logging temporalmente
   */
  disable() {
    this.isDevelopment = false;
  }

  /**
   * Habilitar logging
   */
  enable() {
    this.isDevelopment = process.env.NODE_ENV === "development";
  }
}

// Crear instancia singleton
const logger = new Logger();

// Exportar como default
export default logger;

// También exportar como named export para flexibilidad
export { logger };

/**
 * Hook helper para logging en componentes React
 *
 * Uso:
 *   import { useLogger } from 'utils/logger';
 *
 *   function MyComponent() {
 *     const log = useLogger('MyComponent');
 *
 *     useEffect(() => {
 *       log.info('Component mounted');
 *     }, []);
 *   }
 */
export const useLogger = (componentName) => {
  return {
    log: (...args) => logger.log(`[${componentName}]`, ...args),
    info: (...args) => logger.info(`[${componentName}]`, ...args),
    warn: (...args) => logger.warn(`[${componentName}]`, ...args),
    error: (...args) => logger.error(`[${componentName}]`, ...args),
    debug: (...args) => logger.debug(`[${componentName}]`, ...args),
  };
};
